import { addTokenToMuxVideo } from '@/data/api/fileUpload';
import { useMuxDirectUpload } from '@/hook/useMuxDirectUpload';
import { Box, Button, TextField, Typography, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import MuxUploader from '@mux/mux-uploader-react';
import { useState, useEffect } from 'react';
import MuxVideoPlayer from '@/Pages/VideoPlayer/components/MuxVideoPlayer';
import axiosConfig from '@/data/utils/axiosConfigInstance';
import { UPLOAD_CHOICES } from '@/AdminComponent/AddVideoForm/constants/uploadChoices';
import Toast from '@/utils/toastManager';

export const MuxFileUpload = ({ title, onVideoReady, fileUrl, onFileUrlChange }) => {
	const { mux, setMux, init, startPolling } = useMuxDirectUpload();
	const [videoPreview, setVideoPreview] = useState(null);
	const [uploadMode, setUploadMode] = useState(UPLOAD_CHOICES.ASSET);
	const [assetId, setAssetId] = useState('');
	const titleReady = !!title?.trim();
	
	const createMuxEndpoint = async () => {
		if (!titleReady) return;
		try {
			const r = await init(title.trim());
			if (!r) Toast.error('Failed to create Mux direct upload endpoint.');
		} catch (error) {
			Toast.error(error.message || 'Error creating upload endpoint');
		}
	};
	
	const onMuxUploadSuccess = () => {
		if (!mux.id) {
			Toast.error('No uploadId returned. Check /admin/direct-upload response.');
			return;
		}

		startPolling({
			uploadId: mux.id,
			onReady: (muxVideoLink, duration) => {
				if (onVideoReady) onVideoReady(muxVideoLink, duration);
			}
		});
		setMux(s => ({ ...s, polling: true }));
	};
	
	const onMuxUploadError = event => {
		Toast.error(`Mux upload error: ${event.detail?.message || 'Unknown error'}`);
	};
	
	useEffect(() => {
		const renderVideoPreview = async () => {
			if (!fileUrl) {
				setVideoPreview(null);
				return;
			}

			const isMuxUrl = fileUrl.startsWith('https://stream.mux.com/');
			if (!isMuxUrl) {
				setVideoPreview(null);
				return;
			}

			try {
				const fileWithToken = await addTokenToMuxVideo(fileUrl);
				const videoUrl =
					fileUrl.includes('?token') || !fileWithToken.data?.videoWithToken
						? fileUrl
						: fileWithToken.data.videoWithToken;

				setVideoPreview(
					<Box sx={{ mt: 2 }}>
						<Typography variant="caption" sx={{ mb: 0.5, display: 'block' }}>
							Video Preview
						</Typography>
						<Box sx={{ borderRadius: 1, overflow: 'hidden', border: '1px solid #eee' }}>
							<MuxVideoPlayer
								videoLink={videoUrl}
								videoTitle={title}
								storyboardToken={fileWithToken?.data?.tokenMetaData?.storyboardToken}
							/>
						</Box>
					</Box>
				);
			} catch {
				setVideoPreview(null);
			}
		};

		renderVideoPreview();
	}, [fileUrl]);
	
	const handleFetchAsset = async () => {
		if (!assetId.trim()) {
			Toast.error('Please enter a Mux Asset ID');
			return;
		}

		try {
			const { data } = await axiosConfig.get(
				`${import.meta.env.VITE_APP_API_URL}/admin/mux-asset?assetId=${assetId.trim()}`
			);

			if (!data?.success) {
				Toast.error('Failed to fetch Mux asset data. Please verify the Asset ID.');
				return;
			}

			const { title, muxVideoLink, duration, playback_id } = data.data;

			if (!muxVideoLink) {
				Toast.error('No video link found for this Asset ID.');
				return;
			}
			
			onFileUrlChange(muxVideoLink);
			if (onVideoReady) onVideoReady(muxVideoLink, duration);

			Toast.success(`Fetched Mux asset: ${title || 'Untitled'} (${playback_id})`);
		} catch {
			Toast.error('Error fetching asset data. Please check the console for details.');
		}
	};

	return (
		<Box sx={{ border: '0.06rem dashed #ccc', p: 2, borderRadius: 1 }}>
			<Typography variant="subtitle2" sx={{ mb: 1 }}>
				Mux Video Upload
			</Typography>
			
			<FormControl fullWidth sx={{ mb: 2 }}>
				<InputLabel>Upload Mode</InputLabel>
				<Select value={uploadMode} label="Upload Mode" onChange={e => setUploadMode(e.target.value)}>
					<MenuItem value={UPLOAD_CHOICES.FILE}>Upload Video</MenuItem>
					<MenuItem value={UPLOAD_CHOICES.ASSET}>Upload via Asset ID</MenuItem>
				</Select>
			</FormControl>

			{uploadMode === UPLOAD_CHOICES.FILE && (
				<>
					{!mux.url ? (
						<Button variant="contained" onClick={createMuxEndpoint} disabled={!titleReady || mux.loading}>
							{mux.loading ? 'Preparing…' : 'Create Direct Upload'}
						</Button>
					) : (
						<MuxUploader endpoint={mux.url} onSuccess={onMuxUploadSuccess} onUploadError={onMuxUploadError} />
					)}

					{mux.polling && (
						<Typography variant="body2" color="primary" sx={{ mt: 1 }}>
							Processing video… This can take a few minutes.
						</Typography>
					)}
				</>
			)}

			{uploadMode === UPLOAD_CHOICES.ASSET && (
				<Box display="flex" gap={2} flexDirection="column" sx={{ mt: 1 }}>
					<TextField label="Mux Asset ID" value={assetId} onChange={e => setAssetId(e.target.value)} fullWidth />
					<Button variant="contained" onClick={handleFetchAsset}>
						Fetch Asset
					</Button>
				</Box>
			)}
			
			{fileUrl && (
				<TextField
					sx={{ mt: 2 }}
					label="Video URL"
					value={fileUrl}
					onChange={e => onFileUrlChange && onFileUrlChange(e.target.value)}
					fullWidth
				/>
			)}
			
			{videoPreview}
		</Box>
	);
};
